import React from "react";
import { signed1, toNum } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../useIsPhone";
import { TapPanel, openIndexIn, tapScopeOf, useTapPanel } from "../TapPanel";
import ChartTargets, { columnPlacement } from "./ChartTargets";
import { CHART, PHONE_CHART_WIDTH, ROLE } from "./chartTokens";

// Bars that grow up or down from a midline, one per game (the team's
// Consistency: game-to-game Net rating): bm-positive above the line,
// bm-critical below, heights in proportion to the value, clipped at ±clip
// and scaled to the largest one.
//
//   DivergingBars({ items, clip = 45, format = signed1, ariaLabel, renderDetail, label, phone })
//
// items  [{ key, value, ariaLabel }]: `ariaLabel` is the game in words
//        ("May 15 vs Team A: +46.4, W +35"). A null value draws no bar.
// renderDetail(i) opens the item's detail: a HoverCard on hover or focus of
//   its slot (desktop), a readout above the chart after a tap (phone).
// Desktop is a 560 x 160 SVG; phones a 306 x 140 one (the phone card's
// width).

const SIZES = {
  desktop: { W: 560, H: 160, pad: 4 },
  phone: { W: PHONE_CHART_WIDTH, H: 140, pad: 3 },
};

export const DivergingBars = ({ items = [], clip = 45, format = signed1, ariaLabel, renderDetail, label = "Games", phone: phoneProp }) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  // New items (another team or season) close the readout.
  const tap = useTapPanel(tapScopeOf(items));
  if (!items.length) return null;
  const s = phone ? SIZES.phone : SIZES.desktop;
  const mid = s.H / 2;
  const values = items.map((it) => toNum(it.value));
  const largest = Math.min(clip, Math.max(1, ...values.filter((v) => v !== null).map(Math.abs)));
  const n = items.length;
  const bw = (s.W - 2 * s.pad) / n;
  const openIndex = phone ? openIndexIn(tap, n) : null;

  const targets = items.map((it, i) => ({
    key: it.key ?? i,
    label: it.ariaLabel || format(it.value),
    placement: columnPlacement(i, n, "top"),
    style: { left: `${((s.pad + i * bw) / s.W) * 100}%`, width: `${(bw / s.W) * 100}%`, top: "0%", height: "100%" },
  }));

  return (
    <div className="flex w-full flex-col">
      {phone && renderDetail && (
        <TapPanel id={tap.panelId} open={openIndex !== null} onClose={tap.close} placement="above">
          {openIndex !== null ? renderDetail(openIndex) : null}
        </TapPanel>
      )}
      <div className="relative w-full">
        <svg viewBox={`0 0 ${s.W} ${s.H}`} role="img" aria-label={ariaLabel || label} className="block h-auto w-full">
          {values.map((raw, i) => {
            if (raw === null) return null;
            const v = Math.max(-clip, Math.min(clip, raw));
            const h = Math.max(1, (Math.abs(v) / largest) * (s.H / 2 - 10));
            return (
              <rect
                key={`bar-${items[i].key ?? i}`}
                x={s.pad + i * bw}
                y={v >= 0 ? mid - h : mid}
                width={Math.max(1, bw - 2)}
                height={h}
                rx={1}
                fill={v >= 0 ? ROLE.better : ROLE.worse}
                fillOpacity={openIndex === null || openIndex === i ? 1 : 0.55}
              />
            );
          })}
          <line x1={0} x2={s.W} y1={mid} y2={mid} stroke={CHART.border} strokeWidth={1.5} />
        </svg>
        {renderDetail && <ChartTargets targets={targets} renderDetail={renderDetail} phone={phone} tap={tap} label={label} />}
      </div>
    </div>
  );
};

export default DivergingBars;
